import * as React from 'react';

// PACKAGES
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import CheckListItem from './ChecklistItem';
import SingleSelect from '../../components/SingleSelect';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { SafetyChecklists } from './SafetyChecklistsSubmit';
import { SafetyChecklistApproversStaffMember, SafetyChecklistEditChecklistItem } from '../../api/models';

interface StepOneTypes {
	safetyChecklistsFormData: SafetyChecklists;
	setSafetyChecklistsFormData: React.Dispatch<React.SetStateAction<SafetyChecklists>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	renderedChecklistItems: SafetyChecklistEditChecklistItem[];
	setRenderedChecklistItems: React.Dispatch<React.SetStateAction<SafetyChecklistEditChecklistItem[]>>;
}

const StepOne: React.FC<StepOneTypes> = ({
	safetyChecklistsFormData,
	setSafetyChecklistsFormData,
	setActiveStep,
	renderedChecklistItems,
	setRenderedChecklistItems
}) => {
	// SET UP GENERAL FORM
	const validationSchemaStepOne = Yup.object({
		purposeInput: Yup.string().required('Required')
	});

	const initialValuesStepOne = {
		purposeInput: safetyChecklistsFormData.purpose || ''
	};

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(renderedChecklistItems);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setRenderedChecklistItems(items);
	};

	// ON SUBMIT
	const onSubmitStepOne = (values: any) => {
		setSafetyChecklistsFormData((prevState) => ({
			...prevState,

			purpose: values.purposeInput,
			checklistItems: [...renderedChecklistItems]
		}));

		setActiveStep('step2');
	};

	const addChecklistHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		const newArray = [
			...renderedChecklistItems,
			{
				text: undefined,
				id: uuid()
			}
		];

		setRenderedChecklistItems(newArray);
	};

	const removeHandler = (itemIndex: number) => {
		const newArray = renderedChecklistItems.filter((item, index) => index !== itemIndex);

		setRenderedChecklistItems(newArray);
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validationSchema={validationSchemaStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main w-100">
								<div className="form-group ">
									<label htmlFor="purposeInput" className="label">
										Title
									</label>

									<div className="form-field">
										<Field name="purposeInput" id="purposeInput" type="text" className="form-input" />

										<ErrorMessage name="purposeInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="">
									<label className="label d-block">Checklist items</label>

									{renderedChecklistItems && renderedChecklistItems.length > 0 && (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="checklistItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{renderedChecklistItems.map((item, index) => {
															return (
																<Draggable key={index} draggableId={index.toString()} index={index}>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<CheckListItem
																				item={item}
																				index={index}
																				renderedChecklistItems={renderedChecklistItems}
																				setRenderedChecklistItems={setRenderedChecklistItems}
																				removeHandler={removeHandler}
																			/>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									)}

									<Button
										className="btn-text-icon mbtom-40"
										buttonIcon={<PlusIcon className="link-icon" />}
										onClick={addChecklistHandler}
									>
										Add another item
									</Button>
								</div>
							</div>
						</div>

						<div className="general-divider mtop-30"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepOne;
